import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { clientKeyManager } from '../db/helpers/clientKeyManager';
import {
  UserProfile,
  ProfileVisibility,
  PrivacyCheckResult,
  UserPrivacySettings,
} from '../types/user';

/**
 * V1 Privacy Service
 *
 * Simplified privacy model:
 * - Users can only interact within their organization
 * - Profile visibility (public/private) controls what content org members can see
 * - Cross-org access is always blocked
 * - No connections, no public accounts
 */
export class PrivacyService {
  /**
   * Check if a viewer can view a profile owner's profile
   * V1: Users can only view profiles within their organization
   */
  static async canViewProfile(
    viewer: UserProfile,
    profileOwner: UserProfile,
    _clientKey?: string
  ): Promise<PrivacyCheckResult> {
    try {
      // Super admins can view everything
      if (viewer.role === 'super_admin') {
        return {
          canView: true,
          canMessage: true,
          canReact: true,
        };
      }

      // Users can always view their own profile
      if (viewer.uid === profileOwner.uid) {
        return {
          canView: true,
          canMessage: true,
          canReact: true,
        };
      }

      // V1: Users must be in the same organization
      const sameOrg = viewer.organization_id &&
                      profileOwner.organization_id &&
                      viewer.organization_id === profileOwner.organization_id;

      if (!sameOrg) {
        return {
          canView: false,
          canMessage: false,
          canReact: false,
          reason: 'Must be in the same organization',
        };
      }

      // Same org - can view/message/react
      return {
        canView: true,
        canMessage: true,
        canReact: true,
      };
    } catch (error) {      return {
        canView: false,
        canMessage: false,
        canReact: false,
        reason: 'Error checking permissions',
      };
    }
  }

  /**
   * Check if a sender can message a recipient
   */
  static async canMessage(
    sender: UserProfile,
    recipient: UserProfile,
    clientKey?: string
  ): Promise<boolean> {
    try {
      const result = await this.canViewProfile(sender, recipient, clientKey);
      return result.canMessage;
    } catch (error) {      return false;
    }
  }

  /**
   * Check if a user can react to a post
   */
  static async canReact(
    user: UserProfile,
    postOwner: UserProfile,
    clientKey?: string
  ): Promise<boolean> {
    try {
      const result = await this.canViewProfile(user, postOwner, clientKey);
      return result.canReact;
    } catch (error) {      return false;
    }
  }

  /**
   * Update user's profile visibility
   * V1: Controls what content org members can see (public = full, private = limited)
   */
  static async updateProfileVisibility(
    userId: string,
    visibility: ProfileVisibility,
    clientKey?: string
  ): Promise<void> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const userRef = doc(db, 'clients', clientKey, 'directory', 'users', 'main', userId);
      await updateDoc(userRef, {
        profile_visibility: visibility,
        updated_at: serverTimestamp(),
      });
    } catch (error) {      throw error;
    }
  }

  /**
   * Get user's privacy settings
   */
  static async getPrivacySettings(
    userId: string,
    clientKey?: string
  ): Promise<UserPrivacySettings | null> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const userRef = doc(db, 'clients', clientKey, 'directory', 'users', 'main', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();

      return {
        profile_visibility: userData.profile_visibility || 'public',
      };
    } catch (error) {      return null;
    }
  }

  /**
   * Get which profile fields a viewer can see based on profile visibility
   * V1:
   * - 'public' profile = all fields visible to org members
   * - 'private' profile = only basic fields visible to org members
   */
  static getVisibleFields(viewer: UserProfile, profileOwner: UserProfile): string[] {
    // Same user - all fields
    if (viewer.uid === profileOwner.uid) {
      return ['*'];
    }

    // Super admin - all fields
    if (viewer.role === 'super_admin') {
      return ['*'];
    }

    // Private profile - limited fields
    if (profileOwner.profile_visibility === 'private') {
      return ['uid', 'display_name', 'photo_url', 'role', 'sport', 'team_ids', 'organization_id'];
    }

    // Public profile - all fields visible to org members
    return ['*'];
  }

  /**
   * Helper to check if two users share a team
   */
  static hasSharedTeam(user1: UserProfile, user2: UserProfile): boolean {
    const user1Teams = user1.team_ids || [];
    const user2Teams = user2.team_ids || [];

    return user1Teams.some(teamId => user2Teams.includes(teamId));
  }

  /**
   * Helper to check if two users are in the same organization
   */
  static isSameOrganization(user1: UserProfile, user2: UserProfile): boolean {
    return !!(user1.organization_id &&
              user2.organization_id &&
              user1.organization_id === user2.organization_id);
  }
}
